/** Slow enough to read an excerpt through before it leaves, at the calm end of the ambient range. */
const ROTATION_MS = 15000;

/**
 * `From your notes`: one strip for the whole ranking, walking the notes of the games in it. Automatic
 * rotation alone is a trap for text - it changes while someone is mid-sentence - so the arrows are the
 * control and the timer is the ambient extra that yields to hover, focus and reduced motion.
 *
 * The walk is strictly sequential over the rendered order, which the server already shuffled: that makes
 * one pass a queue where nothing repeats before everything has been seen. Drawing a random note per
 * rotation would produce runs like `A → C → A → A` and read as broken.
 */
export function initRankingNotes(root = globalThis.document, options = {}) {
    const section = root?.querySelector?.('[data-rankings-notes]');
    if (!(section instanceof HTMLElement)) {
        return null;
    }

    const timerApi = options.timerApi ?? globalThis;
    const matchMedia = options.matchMedia ?? globalThis.matchMedia;
    const notes = [...section.querySelectorAll('[data-rankings-note]')];
    const dots = [...section.querySelectorAll('[data-rankings-notes-dot]')];
    // Rendered instead of the dots above the threshold where a dot per note stops being readable. One of
    // the two exists, never both.
    const counter = section.querySelector('[data-rankings-notes-counter]');
    // A single note has nothing to rotate to, and the section renders without the controls in that case.
    if (notes.length < 2) {
        return null;
    }

    const prefersReducedMotion = matchMedia?.('(prefers-reduced-motion: reduce)')?.matches === true;
    let index = 0;
    let handle = null;
    // Two independent reasons to hold the strip, tracked separately: keyboard focus inside it outlasts the
    // pointer leaving, and one flag would let `mouseleave` restart the rotation under a focused link.
    let hovered = false;
    let focused = false;

    const show = (nextIndex) => {
        index = (nextIndex + notes.length) % notes.length;
        notes.forEach((note, noteIndex) => {
            const isActive = noteIndex === index;
            note.classList.toggle('is-active', isActive);
            // The inactive notes share the active one's grid cell, so they have to leave the accessibility
            // tree as well as the picture.
            if (isActive) {
                note.removeAttribute('aria-hidden');
            } else {
                note.setAttribute('aria-hidden', 'true');
            }
        });
        dots.forEach((dot, dotIndex) => dot.classList.toggle('is-active', dotIndex === index));
        if (counter !== null) {
            counter.textContent = index + 1 + ' / ' + notes.length;
        }
    };

    const stop = () => {
        if (handle !== null) {
            timerApi.clearInterval(handle);
            handle = null;
        }
    };

    const start = () => {
        stop();
        // Reduced motion means one note, held: the crossfade is the whole point of the rotation.
        if (prefersReducedMotion || hovered || focused) {
            return;
        }
        handle = timerApi.setInterval(() => show(index + 1), ROTATION_MS);
    };

    const step = (delta) => {
        show(index + delta);
        // A manual step restarts the clock, so the note someone just asked for gets a full interval.
        start();
    };

    section.querySelectorAll('[data-rankings-notes-step]').forEach((button) => {
        button.addEventListener('click', () => step(Number.parseInt(button.dataset.rankingsNotesStep, 10) || 1));
    });

    section.addEventListener('mouseenter', () => {
        hovered = true;
        stop();
    });
    section.addEventListener('mouseleave', () => {
        hovered = false;
        start();
    });
    section.addEventListener('focusin', () => {
        focused = true;
        stop();
    });
    section.addEventListener('focusout', () => {
        focused = false;
        start();
    });

    start();

    return { step, show, stop };
}
